export type SentenceCategory = "standard" | "regulatory" | "eleos" | "philosophy";

export interface SentenceEntry {
  text: string;
  category: SentenceCategory;
  hint?: string;
}

export interface RootEntry {
  word: string;
  transliteration: string;
  hint?: string;
}

export const SENTENCES: Record<number, SentenceEntry[]> = {
  1: [
    { text: "The inspector found a deficiency at the facility.", category: "regulatory" },
    { text: "All licensed operators must comply with HHSC standards.", category: "regulatory" },
    { text: "Some childcare facilities fail their annual inspections.", category: "regulatory" },
    { text: "The supervisor reviewed the report.", category: "standard" },
    { text: "Every document submitted after the deadline is rejected.", category: "standard" },
  ],
  2: [
    { text: "If the facility passes inspection, then the license is renewed.", category: "regulatory" },
    { text: "Either the deficiency was corrected or the facility will be cited.", category: "regulatory" },
    { text: "The operator is not in violation of the cited regulation.", category: "regulatory" },
    { text: "The report is incomplete or the filing was rejected.", category: "standard" },
    { text: "If you are compliant, you will not receive a citation.", category: "regulatory" },
  ],
  3: [
    { text: "All operators who satisfy validity check protocols produce reliable output.", category: "eleos" },
    { text: "Some witness functions run without role-declaration.", category: "eleos" },
    { text: "Every rule that applies to one facility also applies to all comparable facilities.", category: "regulatory" },
    { text: "There exists at least one inspector who has misapplied this regulation.", category: "regulatory" },
    { text: "No finding that lacks documentary evidence can survive administrative review.", category: "regulatory" },
  ],
  4: [
    { text: "The operator necessarily knows that the filing deadline is May 15.", category: "regulatory" },
    { text: "It is possible that the inspector's finding was based on a misread of the standard.", category: "regulatory" },
    { text: "The inspector believes that the original citation and new claim overlap.", category: "regulatory" },
    { text: "The operator must have known about the deficiency before the inspection.", category: "regulatory" },
    { text: "It is impossible for a facility to be simultaneously compliant and cited for the same violation.", category: "regulatory" },
  ],
  5: [
    { text: "If the inspector's framing claims continuity, then either the original issue and new claim must overlap, or the framing is fabricated.", category: "regulatory" },
    { text: "All inspectors who follow procedure produce valid findings; this inspector followed procedure; therefore this finding is valid.", category: "regulatory", hint: "categorical syllogism" },
    { text: "Logos operates via structural isomorphism; ELEOS encodes logos; therefore ELEOS recodes functional reality.", category: "eleos", hint: "hypothetical syllogism" },
    { text: "Every operator cited under section 746.3701 has the right to appeal; this operator was cited; therefore this operator has the right to appeal.", category: "regulatory", hint: "modus ponens with universal premise" },
  ],
  6: [
    { text: "The operator's continued failure to comply demonstrates a pattern of disregard for child safety.", category: "regulatory", hint: "look for hidden presuppositions" },
    { text: "Since all great systems are iterative, and ELEOS is iterative, ELEOS must be a great system.", category: "eleos", hint: "undistributed middle" },
    { text: "If you were truly compliant, you wouldn't need to appeal.", category: "regulatory" },
    { text: "Either you accept our findings or you are obstructing the regulatory process.", category: "regulatory", hint: "false dichotomy variant" },
  ],
};

export const ROOTS: Record<number, RootEntry[]> = {
  1: [
    { word: "ashrei", transliteration: "ashrei", hint: "entry point — your name's root" },
    { word: "shalom", transliteration: "shalom", hint: "complete / whole / peace" },
    { word: "emet", transliteration: "emet", hint: "truth / faithfulness" },
    { word: "lamad", transliteration: "lamad", hint: "the Talmud root — learn / teach" },
    { word: "katav", transliteration: "katav", hint: "the writing root" },
  ],
  2: [
    { word: "chatam", transliteration: "chatam", hint: "Account-Ability Architecture — the seal" },
    { word: "edut", transliteration: "edut", hint: "testimony / evidence" },
    { word: "geniza", transliteration: "geniza", hint: "the sacred archive" },
    { word: "goel", transliteration: "goel", hint: "rights-restorer / redeemer" },
    { word: "neeman", transliteration: "neeman", hint: "faithful / trustworthy record" },
  ],
  3: [
    { word: "cheshbon", transliteration: "cheshbon", hint: "account / reckoning — same root as computer" },
    { word: "shamar", transliteration: "shamar", hint: "to guard AND to observe — same root" },
    { word: "shalshelet", transliteration: "shalshelet", hint: "chain / sequence — appears 4x in Torah" },
    { word: "gevul", transliteration: "gevul", hint: "border / constraint / validity gate" },
    { word: "teshuvah", transliteration: "teshuvah", hint: "return / restore — the restore function" },
  ],
  4: [
    { word: "he'emid", transliteration: "he'emid", hint: "Hif'il causative — identify the root" },
    { word: "nichtav", transliteration: "nichtav", hint: "Nif'al passive — K-T-B root" },
    { word: "hitchashev", transliteration: "hitchashev", hint: "Hitpa'el reflexive — identify root + function" },
    { word: "meushar", transliteration: "meushar", hint: "your root — which binyan is this?" },
    { word: "hechzir", transliteration: "hechzir", hint: "Hif'il of SH-V-R — restore function" },
  ],
  5: [
    { word: "sijill", transliteration: "sijill", hint: "Arabic cognate — official sealed record" },
    { word: "pramana", transliteration: "pramana", hint: "Sanskrit — valid evidence / proof authority" },
    { word: "ne'emanut", transliteration: "ne'emanut", hint: "faithfulness — abstract noun from emet root" },
    { word: "achrayut", transliteration: "achrayut", hint: "responsibility / accountability — trace the root" },
  ],
  6: [
    { word: "tzedek", transliteration: "tzedek", hint: "justice / rightness — Aetherion mapping" },
    { word: "yosher", transliteration: "yosher", hint: "uprightness / integrity — Y-SH-R family" },
    { word: "atzmai", transliteration: "atzmai", hint: "independent / sovereign — self-root" },
    { word: "chakak", transliteration: "chakak", hint: "to engrave AND to legislate — same root" },
  ],
};

export const BINYAN_REFERENCE = [
  { name: "Qal / Pa'al", fn: "Basic action", ex: "katav — he wrote" },
  { name: "Nif'al", fn: "Passive / reflexive", ex: "nichtav — it was written" },
  { name: "Pi'el", fn: "Intensive / factitive", ex: "diber — he spoke (intensive of davar)" },
  { name: "Pu'al", fn: "Passive of Pi'el", ex: "meushar — was approved/blessed" },
  { name: "Hif'il", fn: "Causative: make X do", ex: "hichtiv — to dictate (make write)" },
  { name: "Huf'al", fn: "Passive of Hif'il", ex: "huchtar — was crowned" },
  { name: "Hitpa'el", fn: "Reflexive: do to yourself", ex: "hitkatev — to correspond" },
];

export function getRandomSentence(level: number, category?: SentenceCategory): string {
  const pool = SENTENCES[level] ?? SENTENCES[3];
  const filtered = category ? pool.filter((s) => s.category === category) : pool;
  const source = filtered.length > 0 ? filtered : pool;
  return source[Math.floor(Math.random() * source.length)].text;
}

export function getRandomRoot(level: number): string {
  const pool = ROOTS[level] ?? ROOTS[3];
  return pool[Math.floor(Math.random() * pool.length)].transliteration;
}

export function detectMultiSentence(text: string): boolean {
  return text.split(/[.;]/).filter((s) => s.trim().length > 20).length >= 3;
}

// Compatibility alias for App.tsx
export function getRandom(level: number, mode: string = 'standard'): string {
  if (mode === 'roots') return getRandomRoot(level);
  return getRandomSentence(level);
}
